import { useEffect, useState, useCallback } from "react";
import {
  BarChart3, Trophy, XCircle, CircleDot, Clock, TrendingUp, Users, Briefcase,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls = "bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-colors";

const PERIODS = [
  { value: "7", label: "Ultimi 7 giorni" },
  { value: "30", label: "Ultimi 30 giorni" },
  { value: "90", label: "Ultimi 90 giorni" },
  { value: "365", label: "Ultimo anno" },
  { value: "all", label: "Tutto lo storico" },
  { value: "custom", label: "Personalizzato" },
];

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-line rounded-[8px] p-4">
      <div className="flex items-center gap-2 text-subtle mb-2">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="text-2xl font-semibold text-ink tracking-tight">{value}</p>
      {sub && <p className="text-xs text-faint mt-0.5">{sub}</p>}
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-line rounded-[8px] p-5">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-subtle" strokeWidth={1.5} />}
        <h2 className="text-base font-semibold text-ink">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function BreakdownTable({ rows, firstCol, testid }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead>
          <tr className="text-left text-xs text-subtle border-b border-line">
            <th className="py-2 font-semibold">{firstCol}</th>
            <th className="py-2 font-semibold text-right">Totali</th>
            <th className="py-2 font-semibold text-right">Vinte</th>
            <th className="py-2 font-semibold text-right">Perse</th>
            <th className="py-2 font-semibold text-right">Aperte</th>
            <th className="py-2 font-semibold text-right">Chiusura %</th>
            <th className="py-2 font-semibold text-right">Win %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className="border-b border-line last:border-0">
              <td className="py-2.5 font-medium text-ink">{r.name}</td>
              <td className="py-2.5 text-right">{r.total}</td>
              <td className="py-2.5 text-right text-brand-dark font-medium">{r.won}</td>
              <td className="py-2.5 text-right text-destructive">{r.lost}</td>
              <td className="py-2.5 text-right text-subtle">{r.open}</td>
              <td className="py-2.5 text-right font-medium">{r.close_rate}%</td>
              <td className="py-2.5 text-right text-subtle">{r.win_rate}%</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={7} className="py-6 text-center text-faint text-sm">Nessun dato</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [period, setPeriod] = useState("90");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [positionId, setPositionId] = useState("");
  const [userId, setUserId] = useState("");
  const [positions, setPositions] = useState([]);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/positions").then((r) => setPositions(r.data)).catch(() => {});
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(() => {
    const params = {};
    if (positionId) params.position_id = positionId;
    if (isAdmin && userId) params.assigned_to = userId;
    if (period === "custom") {
      if (custom.from) params.date_from = new Date(custom.from + "T00:00:00").toISOString();
      if (custom.to) { const d = new Date(custom.to + "T23:59:59"); params.date_to = d.toISOString(); }
    } else if (period !== "all") {
      const d = new Date(); d.setDate(d.getDate() - parseInt(period, 10)); d.setHours(0, 0, 0, 0);
      params.date_from = d.toISOString();
    }
    setLoading(true);
    api.get("/dashboard/stats", { params }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [period, custom, positionId, userId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const k = data?.kpi;

  return (
    <div className="flex flex-col gap-6 max-w-[1100px]" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Dashboard</h1>
        <p className="text-xs text-subtle mt-0.5">
          Insight su conversione, account e posizioni{!isAdmin && " — dati delle tue opportunità"}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-line rounded-[8px] p-3">
        <select data-testid="filter-period" value={period} onChange={(e) => setPeriod(e.target.value)} className={inputCls}>
          {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        {period === "custom" && (
          <>
            <input data-testid="filter-date-from" type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className={inputCls} />
            <span className="text-subtle text-sm">→</span>
            <input data-testid="filter-date-to" type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className={inputCls} />
          </>
        )}
        <select data-testid="filter-position" value={positionId} onChange={(e) => setPositionId(e.target.value)} className={inputCls}>
          <option value="">Tutte le posizioni</option>
          {positions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {isAdmin && (
          <select data-testid="filter-user" value={userId} onChange={(e) => setUserId(e.target.value)} className={inputCls}>
            <option value="">Tutti i sales</option>
            <option value="unassigned">Non assegnate</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
      </div>

      {loading || !k ? (
        <p className="text-sm text-subtle">Caricamento…</p>
      ) : (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi testid="kpi-total" icon={BarChart3} label="Opportunità" value={k.total} />
            <Kpi testid="kpi-won" icon={Trophy} label="Vinte" value={k.won} />
            <Kpi testid="kpi-lost" icon={XCircle} label="Perse" value={k.lost} />
            <Kpi testid="kpi-open" icon={CircleDot} label="Aperte" value={k.open} />
            <Kpi testid="kpi-close-rate" icon={TrendingUp} label="Tasso chiusura" value={`${k.close_rate}%`} sub="Vinte / (Vinte+Perse)" />
            <Kpi testid="kpi-win-rate" icon={TrendingUp} label="Win rate" value={`${k.win_rate}%`} sub="Vinte / Totali" />
          </div>
          {k.avg_days_to_win != null && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Kpi testid="kpi-avg-days" icon={Clock} label="Tempo medio di chiusura (vinte)" value={`${k.avg_days_to_win} giorni`} />
            </div>
          )}

          {/* Funnel */}
          <Card title="Conversione per fase" icon={TrendingUp}>
            <div className="flex flex-col gap-3">
              {data.funnel.map((f) => (
                <div key={f.key} data-testid={`funnel-${f.key}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-ink">{f.name}</span>
                    <span className="text-subtle flex items-center gap-2">
                      <span className="font-medium text-ink">{f.reached}</span>
                      <span className="text-xs text-brand-dark font-medium">{f.pct_of_total}% del totale</span>
                      {f.conv_from_prev != null && (
                        <span className="text-xs text-faint">· {f.conv_from_prev}% dalla precedente</span>
                      )}
                      {f.avg_days_in_stage != null && (
                        <span className="text-xs text-faint">· {f.avg_days_in_stage}gg in fase</span>
                      )}
                    </span>
                  </div>
                  <div className="h-6 bg-background rounded-[4px] overflow-hidden">
                    <div
                      className={`h-full rounded-[4px] transition-all ${f.marks_won ? "bg-brand-dark" : "bg-brand"}`}
                      style={{ width: `${Math.max(2, f.pct_of_total)}%` }}
                    />
                  </div>
                </div>
              ))}
              {data.funnel.length === 0 && <p className="text-sm text-faint">Nessuna fase configurata</p>}
            </div>
          </Card>

          {/* Breakdown by account */}
          <Card title="Breakdown per account (sales)" icon={Users}>
            <BreakdownTable rows={data.by_account} firstCol="Sales" testid="table-by-account" />
          </Card>

          {/* Breakdown by position */}
          <Card title="Breakdown per posizione" icon={Briefcase}>
            <BreakdownTable rows={data.by_position} firstCol="Posizione" testid="table-by-position" />
          </Card>
        </>
      )}
    </div>
  );
}
